import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";

interface AnimatedWordsProps {
  words: string[];
  interval?: number;
  colors?: string[];
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

export function AnimatedWords({ 
  words,
  interval = 3000,
  colors = ["bg-ss-blue"],
  variant = "primary", 
  size = "md", 
  className,
}: AnimatedWordsProps) {
  const [mounted, setMounted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setMounted(true);
    
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % words.length);
    }, interval);

    return () => clearInterval(intervalId);
  }, [words.length, interval]);

  const baseStyles = "relative inline-flex items-center justify-center font-medium transition-all duration-300 ease-in-out overflow-hidden";
  
  const variants = {
    primary: "bg-ss-light-green text-black! font-semibold rounded-3xl shadow",
    outline: "border lg:border-2 border-ss-light-green font-semibold bg-white text-black! rounded-3xl shadow-sm",
    ghost: "border-2 border-ss-light-green text-white! font-semibold bg-transparent rounded-3xl"
  };
  
  const sizes = {
    sm: "w-32 md:w-36 px-2 py-1 text-xs md:px-3 md:py-1 md:text-sm",
    md: "w-32 md:w-36 px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-base! lg:px-8 lg:py-3",
    lg: "px-3 py-0.5 text-[35px] lg:px-4 lg:py-1 lg:text-[64px] md:px-4 md:py-1 md:text-[55px] sm:text-[45px]",
    xl: "px-6 py-3 text-lg md:px-10 md:py-4 md:text-xl lg:px-12 lg:py-5 lg:text-2xl"
  };
  
  const currentWord = mounted ? words[currentIndex] : words[0];
  const currentColor = mounted && colors[currentIndex] ? colors[currentIndex] : colors[0];
  
  // Check if all words are single words (no spaces) for fixed width calculation
  const allSingleWords = words.every(word => !word.includes(' '));
  
  // Calculate fixed width for single words based on the longest word
  const getFixedWidth = () => {
    if (!allSingleWords) return '';
    
    // Find the longest word to determine the minimum width needed
    const longestWord = words.reduce((longest, current) => 
      current.length > longest.length ? current : longest
    );
    
    // Estimate width based on character count and size
    // These are rough estimates - adjust as needed
    const baseWidth = longestWord.length * 0.6; // Base character width
    
    if (size === 'lg') {
      return `w-[${Math.max(baseWidth * 16, 120)}px]`; // Minimum 120px, scale with characters
    } else if (size === 'md') {
      return `w-[${Math.max(baseWidth * 12, 100)}px]`;
    } else if (size === 'sm') {
      return `w-[${Math.max(baseWidth * 10, 80)}px]`;
    } else if (size === 'xl') {
      return `w-[${Math.max(baseWidth * 20, 140)}px]`;
    }
    
    return '';
  };

  return (
    <div
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        allSingleWords ? getFixedWidth() : '',
        currentColor,
        // Hide on small screens when size is 'sm'
        size === 'sm' ? 'hidden md:inline-flex' : '',
        className
      )}
    >
      {/* Grainy texture overlay - only for non-outline variants */}
      {variant !== "outline" && (
        <div 
          className="min-w-fit w-fit max-w-fit cursor-pointer absolute inset-0 opacity-40 pointer-events-none rounded-3xl" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundSize: '150px 150px'
          }}
        />
      )}
      <span className="relative z-10">
        <AnimatePresence mode="wait">
          <motion.span
            key={currentWord}
            initial={{ opacity: 0, y: 25, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -25, scale: 0.95 }}
            transition={{ 
              duration: 0.5, 
              ease: [0.25, 0.46, 0.45, 0.94],
              opacity: { duration: 0.3 },
              scale: { duration: 0.4 }
            }}
            className="inline-block"
          >
            {currentWord}
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  );
}
